import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const TenantContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

export function TenantProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [contexts, setContexts] = useState([]);
  const [activeTenantId, setActiveTenantId] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [portals, setPortals] = useState(['player_app']);
  const [loading, setLoading] = useState(true);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchContexts = useCallback(async () => {
    if (!user) {
      // Only set loading=false if auth is done and there's truly no user
      if (!authLoading) {
        setLoading(false);
        setHasFetched(false);
      }
      return;
    }
    setLoading(true);
    try {
      const ctxRes = await axios.get(`${API_URL}/api/rbac/contexts`, { withCredentials: true });
      // Defensive: WKWebView can hand axios a raw string body. Parse if needed.
      let ctxData = ctxRes.data;
      if (typeof ctxData === 'string') { try { ctxData = JSON.parse(ctxData); } catch { ctxData = {}; } }
      setContexts(Array.isArray(ctxData?.contexts) ? ctxData.contexts : []);
      setActiveTenantId(ctxData?.active_tenant_id || null);
      const portalsRes = await axios.get(`${API_URL}/api/rbac/portals`, { withCredentials: true });
      let portalsData = portalsRes.data;
      if (typeof portalsData === 'string') { try { portalsData = JSON.parse(portalsData); } catch { portalsData = {}; } }
      setPortals(Array.isArray(portalsData?.portals) ? portalsData.portals : ['player_app']);
      setHasFetched(true);
    } catch (err) {
      console.warn('[Tenant] fetchContexts failed', err?.response?.status, err?.message);
      setContexts([]);
      setHasFetched(true);
    } finally {
      setLoading(false);
    }
  }, [user, authLoading]);

  const fetchPermissions = useCallback(async () => {
    if (!user || !activeTenantId) { setPermissions([]); return; }
    try {
      const { data } = await axios.get(`${API_URL}/api/rbac/permissions?tenant_id=${activeTenantId}`, { withCredentials: true });
      let pd = data;
      if (typeof pd === 'string') { try { pd = JSON.parse(pd); } catch { pd = {}; } }
      setPermissions(Array.isArray(pd?.permissions) ? pd.permissions : []);
    } catch { setPermissions([]); }
  }, [user, activeTenantId]);

  const fetchInvites = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/rbac/invitations/pending`, { withCredentials: true });
      let inv = data;
      if (typeof inv === 'string') { try { inv = JSON.parse(inv); } catch { inv = []; } }
      setPendingInvites(Array.isArray(inv) ? inv : []);
    } catch { setPendingInvites([]); }
  }, [user]);

  useEffect(() => { fetchContexts(); }, [fetchContexts]);
  useEffect(() => { fetchPermissions(); }, [fetchPermissions]);
  useEffect(() => { fetchInvites(); }, [fetchInvites]);

  const switchContext = async (tenantId) => {
    try {
      await axios.post(`${API_URL}/api/rbac/switch-context`, { tenant_id: tenantId }, { withCredentials: true });
      setActiveTenantId(tenantId);
    } catch {}
  };

  const activeContext = contexts.find(c => c.tenant_id === activeTenantId) || null;

  const hasPermission = (code) => permissions.includes(code);
  const hasPortal = (portal) => portals.includes(portal);
  const hasRole = (roleCode) => contexts.some(c => c.roles.includes(roleCode));

  const acceptInvite = async (invId) => {
    await axios.post(`${API_URL}/api/rbac/invitations/${invId}/accept`, {}, { withCredentials: true });
    fetchContexts();
    fetchInvites();
  };
  const declineInvite = async (invId) => {
    await axios.post(`${API_URL}/api/rbac/invitations/${invId}/decline`, {}, { withCredentials: true });
    fetchInvites();
  };

  // Loading should be true while auth is loading OR while contexts haven't been fetched
  const isLoading = authLoading || loading || (!hasFetched && !!user);

  return (
    <TenantContext.Provider value={{
      contexts, activeTenantId, activeContext, permissions, portals, loading: isLoading,
      pendingInvites, switchContext, hasPermission, hasPortal, hasRole,
      acceptInvite, declineInvite, refreshContexts: fetchContexts,
    }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) throw new Error('useTenant must be used within a TenantProvider');
  return context;
}
